/**
 * CoverageModelFormModal - Create/Edit Coverage Model
 *
 * Modal form to create or edit a coverage model with guarantee selection.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Shield, Check, Loader2 } from 'lucide-react';
import api from '../../../api.js';

// Coverage level options — le libellé se résout au rendu (t), la couleur reste ici.
const LEVEL_OPTIONS = [
  { value: 'tous_risques', color: '#3B82F6' },
  { value: 'tiers_plus', color: '#6B7280' },
  { value: 'tiers_simple', color: '#9CA3AF' },
  { value: 'economique', color: '#D97706' },
  { value: 'custom', color: '#8B5CF6' },
];

function CoverageModelFormModal({ isOpen, onClose, model, onMessage }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEditing = !!model;

  // Form state
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    short_description: '',
    description: '',
    level: 'custom',
    default_deductible: 0,
    is_recommended: false,
    is_active: true,
  });
  const [selectedGuarantees, setSelectedGuarantees] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch guarantees
  const { data: guaranteesData, isLoading: loadingGuarantees } = useQuery({
    queryKey: ['guarantee_template'],
    queryFn: () => api.getAll('guarantee_template'),
    enabled: isOpen,
  });

  // Fetch existing links if editing
  const { data: linksData } = useQuery({
    queryKey: ['coverage_model_guarantee', model?.id],
    queryFn: () => api.getAll('coverage_model_guarantee', { coverage_model_id: model.id }),
    enabled: isOpen && isEditing,
  });

  const guarantees = guaranteesData?.data || guaranteesData || [];
  const links = linksData?.data || linksData || [];

  // Initialize form when model changes
  useEffect(() => {
    if (model) {
      setFormData({
        code: model.code || '',
        name: model.name || '',
        short_description: model.short_description || '',
        description: model.description || '',
        level: model.level || 'custom',
        default_deductible: model.default_deductible || 0,
        is_recommended: model.is_recommended || false,
        is_active: model.is_active !== false,
      });
    } else {
      setFormData({
        code: '',
        name: '',
        short_description: '',
        description: '',
        level: 'custom',
        default_deductible: 0,
        is_recommended: false,
        is_active: true,
      });
      setSelectedGuarantees([]);
    }
    setErrors({});
  }, [model, isOpen]);

  // Initialize selected guarantees when links load
  useEffect(() => {
    if (links.length > 0) {
      setSelectedGuarantees(links.map(l => l.guarantee_id));
    }
  }, [links]);

  // Group guarantees by category
  const groupedGuarantees = guarantees.reduce((acc, g) => {
    const cat = g.category || 'dommages';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(g);
    return acc;
  }, {});

  // Handle input change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Handle guarantee selection
  const toggleGuarantee = (guaranteeId) => {
    setSelectedGuarantees(prev => {
      if (prev.includes(guaranteeId)) {
        return prev.filter(id => id !== guaranteeId);
      } else {
        return [...prev, guaranteeId];
      }
    });
  };

  // Validate form
  const validate = () => {
    const newErrors = {};
    if (!formData.code.trim()) newErrors.code = t('insuranceConfig.coverageModelForm.errorCodeRequired');
    if (!formData.name.trim()) newErrors.name = t('insuranceConfig.coverageModelForm.errorNameRequired');
    if (!formData.level) newErrors.level = t('insuranceConfig.coverageModelForm.errorLevelRequired');
    if (selectedGuarantees.length === 0) newErrors.guarantees = t('insuranceConfig.coverageModelForm.errorGuaranteesRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      // Get color from level
      const levelOption = LEVEL_OPTIONS.find(l => l.value === formData.level);
      const modelData = {
        ...formData,
        code: formData.code.toUpperCase().replace(/\s+/g, '_'),
        default_deductible: parseFloat(formData.default_deductible) || 0,
        color_code: levelOption?.color || '#6B7280',
        icon: 'shield',
      };

      let modelId;

      if (isEditing) {
        // Update existing model
        await api.update('coverage_model', model.id, modelData);
        modelId = model.id;

        // Delete existing links
        for (const link of links) {
          await api.delete('coverage_model_guarantee', link.id);
        }
      } else {
        // Create new model
        const result = await api.create('coverage_model', modelData);
        if (!result.ok) {
          throw new Error(result.error?.message || t('insuranceConfig.coverageModelForm.createError'));
        }
        modelId = result.data.id;
      }

      // Create guarantee links
      for (const guaranteeId of selectedGuarantees) {
        await api.create('coverage_model_guarantee', {
          coverage_model_id: modelId,
          guarantee_id: guaranteeId,
          inclusion_type: 'included',
        });
      }

      // Refresh data
      queryClient.invalidateQueries(['coverage_model']);
      queryClient.invalidateQueries(['coverage_model_guarantee']);

      onMessage?.({
        type: 'success',
        text: isEditing
          ? t('insuranceConfig.coverageModelForm.updatedSuccess')
          : t('insuranceConfig.coverageModelForm.createdSuccess'),
      });
      onClose();

    } catch (error) {
      console.error('Error saving coverage model:', error);
      onMessage?.({
        type: 'error',
        text: error.message || t('insuranceConfig.toast.saveError'),
      });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditing ? t('insuranceConfig.coverageModelForm.titleEdit') : t('insuranceConfig.coverageModelForm.titleCreate')}
              </h2>
              <p className="text-sm text-gray-500">
                {t('insuranceConfig.coverageModelForm.subtitle')}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('insuranceConfig.coverageModelForm.code')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  placeholder="TOUS_RISQUES"
                  className={`w-full px-3 py-2 border rounded-lg text-sm ${
                    errors.code ? 'border-red-500' : 'border-gray-300'
                  } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
                {errors.code && <p className="text-red-500 text-xs mt-1">{errors.code}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('insuranceConfig.coverageModelForm.name')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('insuranceConfig.coverageModelForm.namePlaceholder')}
                  className={`w-full px-3 py-2 border rounded-lg text-sm ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('insuranceConfig.coverageModelForm.level')} <span className="text-red-500">*</span>
                </label>
                <select
                  name="level"
                  value={formData.level}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {LEVEL_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{t(`insuranceConfig.levels.${opt.value}`)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('insuranceConfig.coverageModelForm.defaultDeductible')}
                </label>
                <input
                  type="number"
                  name="default_deductible"
                  value={formData.default_deductible}
                  onChange={handleChange}
                  min="0"
                  step="50"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('insuranceConfig.coverageModelForm.shortDescription')}
              </label>
              <input
                type="text"
                name="short_description"
                value={formData.short_description}
                onChange={handleChange}
                placeholder={t('insuranceConfig.coverageModelForm.shortDescriptionPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('insuranceConfig.coverageModelForm.description')}
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={2}
                placeholder={t('insuranceConfig.coverageModelForm.descriptionPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_recommended"
                  checked={formData.is_recommended}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-sm text-gray-700">{t('insuranceConfig.coverageModelForm.isRecommended')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-sm text-gray-700">{t('insuranceConfig.coverageModelForm.isActive')}</span>
              </label>
            </div>

            {/* Guarantee Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('insuranceConfig.coverageModelForm.guarantees')} <span className="text-red-500">*</span>
                <span className="ml-2 text-gray-500 font-normal">
                  {t('insuranceConfig.coverageModelForm.selectedCount', { count: selectedGuarantees.length })}
                </span>
              </label>
              {errors.guarantees && <p className="text-red-500 text-xs mb-2">{errors.guarantees}</p>}

              {loadingGuarantees ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-64 overflow-y-auto">
                  {Object.entries(groupedGuarantees).map(([category, catGuarantees]) => (
                    <div key={category} className="p-3">
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                        {t(`insuranceConfig.categories.${category}`, { defaultValue: category })}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {catGuarantees
                          .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                          .map(g => {
                            const isSelected = selectedGuarantees.includes(g.id);
                            return (
                              <button
                                key={g.id}
                                type="button"
                                onClick={() => toggleGuarantee(g.id)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                                  isSelected
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                                title={g.description}
                              >
                                {isSelected && <Check className="w-3 h-3" />}
                                {g.name}
                              </button>
                            );
                          })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {isEditing ? t('common.actions.save') : t('insuranceConfig.coverageModelForm.submitCreate')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CoverageModelFormModal;
